import { z } from 'zod';
import { CORE_ENTITIES } from './entities.js';
export const FIELD_TYPES = [
    'text',
    'long_text',
    'number',
    'currency',
    'percent',
    'boolean',
    'date',
    'datetime',
    'select',
    'multi_select',
    'email',
    'phone',
    'url',
    'json',
];
export const KEY_REGEX = /^[a-z][a-z0-9_]{0,40}$/;
export const SelectOption = z.object({
    value: z.string().min(1).max(60),
    label: z.string().min(1).max(80).optional(),
    color: z.string().max(30).optional(),
});
export const FieldValidation = z
    .object({
    min: z.number().optional(),
    max: z.number().optional(),
    min_length: z.number().int().optional(),
    max_length: z.number().int().optional(),
    pattern: z.string().optional(),
})
    .partial();
export const FieldDefinitionInput = z.object({
    entity: z.enum(CORE_ENTITIES),
    key: z.string().regex(KEY_REGEX, 'snake_case, starts with a letter, max 41 chars'),
    label: z.string().min(1).max(80),
    type: z.enum(FIELD_TYPES),
    options: z.array(SelectOption).min(1).optional(),
    required: z.boolean().default(false),
    default_value: z.unknown().optional(),
    description: z.string().max(500).optional(),
    validation: FieldValidation.optional(),
    show_in_list: z.boolean().default(false),
    position: z.number().int().default(0),
});
/** SQL cast used when a custom field appears in a report or a rule condition. */
export function fieldSqlType(type) {
    switch (type) {
        case 'number':
        case 'currency':
        case 'percent':
            return 'numeric';
        case 'boolean':
            return 'boolean';
        case 'date':
            return 'date';
        case 'datetime':
            return 'timestamptz';
        case 'json':
        case 'multi_select':
            return 'jsonb';
        default:
            return 'text';
    }
}
/** Coerce a loosely-typed value (usually from an LLM) into the field's storage type. */
export function coerceFieldValue(def, raw) {
    if (raw === null || raw === undefined || raw === '')
        return null;
    switch (def.type) {
        case 'number':
        case 'currency':
        case 'percent': {
            const n = typeof raw === 'number' ? raw : Number(String(raw).replace(/[$,%\s]/g, ''));
            if (!Number.isFinite(n))
                throw new Error(`Expected a number, got ${JSON.stringify(raw)}`);
            return n;
        }
        case 'boolean': {
            if (typeof raw === 'boolean')
                return raw;
            const s = String(raw).toLowerCase();
            if (['true', 'yes', 'y', '1'].includes(s))
                return true;
            if (['false', 'no', 'n', '0'].includes(s))
                return false;
            throw new Error(`Expected true/false, got ${JSON.stringify(raw)}`);
        }
        case 'select': {
            const s = String(raw);
            const opts = def.options ?? [];
            const hit = opts.find((o) => o.value === s) ?? opts.find((o) => (o.label ?? o.value).toLowerCase() === s.toLowerCase());
            if (!hit)
                throw new Error(`"${s}" is not one of: ${opts.map((o) => o.value).join(', ')}`);
            return hit.value;
        }
        case 'multi_select': {
            const arr = Array.isArray(raw) ? raw : String(raw).split(',').map((x) => x.trim());
            return arr.map((v) => coerceFieldValue({ type: 'select', options: def.options }, v));
        }
        case 'json':
            return raw;
        default:
            return String(raw);
    }
}
// ----------------------------------------------------------------------------
// Entity links
// ----------------------------------------------------------------------------
export const LinkDefinitionInput = z.object({
    key: z.string().regex(KEY_REGEX),
    label: z.string().min(1).max(80),
    inverse_label: z.string().max(80).optional(),
    from_entity: z.enum(CORE_ENTITIES),
    to_entity: z.enum(CORE_ENTITIES),
    cardinality: z.enum(['one', 'many']).default('one'),
    description: z.string().max(500).optional(),
});
//# sourceMappingURL=fields.js.map